import axios from 'axios';
import { logError } from '../utils/errorHandlers';

export class DuplicateRegistrationError extends Error {
    constructor(message = 'Registration already exists', details) {
        super(message);
        this.name = 'DuplicateRegistrationError';
        this.isDuplicate = true;
        if (details) {
            this.details = details;
        }
    }
}

function getRegistrationsApiUrl() {
    return process.env.REACT_APP_REGISTRATIONS_API_URL;
}

function ensureRegistrationsApiUrl() {
    const apiUrl = getRegistrationsApiUrl();

    if (!apiUrl) {
        const error = new Error('Missing REACT_APP_REGISTRATIONS_API_URL environment variable');
        logError('[registrationService] Missing REACT_APP_REGISTRATIONS_API_URL', error);
        throw error;
    }

    return apiUrl;
}

function buildRegistrationsUrl(searchParams = null) {
    const apiUrl = ensureRegistrationsApiUrl();
    const url = new URL(apiUrl);

    if (searchParams && typeof searchParams === 'object') {
        Object.entries(searchParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.set(key, value);
            }
        });
    }

    return url.toString();
}

function normalizeApiError(error, fallbackMessage = 'Registration request failed') {
    if (error instanceof DuplicateRegistrationError) {
        return error;
    }

    const status = error?.response?.status;
    const responseMessage = error?.response?.data?.message;

    if (status === 409) {
        return new DuplicateRegistrationError(responseMessage || 'Registration already exists', error?.response?.data);
    }

    if (!error?.response) {
        return error instanceof Error ? error : new Error(fallbackMessage);
    }

    const normalizedError = new Error(responseMessage || fallbackMessage);
    normalizedError.response = error.response;
    return normalizedError;
}

export async function fetchRegistrations(tournamentId) {
    if (!tournamentId) {
        throw new Error('[registrationService] tournamentId is required to fetch registrations');
    }

    const url = buildRegistrationsUrl({ tournamentId });

    try {
        const response = await axios.get(url);
        return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
        logError(`[registrationService] Failed to fetch registrations for tournamentId=${tournamentId}`, error);
        throw normalizeApiError(error, 'Failed to fetch registrations');
    }
}

async function postRegistration(payload, contextLabel) {
    const url = buildRegistrationsUrl();

    try {
        const response = await axios.post(url, payload);

        if (response?.status !== 201) {
            throw new Error(`Unexpected response status: ${response?.status ?? 'unknown'}`);
        }

        return response?.data;
    } catch (error) {
        logError(`[registrationService] Failed to ${contextLabel}`, error);
        throw normalizeApiError(error, `Failed to ${contextLabel}`);
    }
}

export async function registerOE({ tournamentId, tournamentDate, player1Name }) {
    if (!tournamentId || !tournamentDate || !player1Name) {
        throw new Error('[registrationService] tournamentId, tournamentDate and player1Name are required for OE registration');
    }

    return postRegistration(
        {
            tournamentId,
            tournamentDate,
            type: 'OE',
            player1Name,
        },
        'register OE player'
    );
}

export async function registerOD({ tournamentId, tournamentDate, player1Name, player2Name }) {
    if (!tournamentId || !tournamentDate || !player1Name || !player2Name) {
        throw new Error('[registrationService] tournamentId, tournamentDate, player1Name and player2Name are required for OD registration');
    }

    return postRegistration(
        {
            tournamentId,
            tournamentDate,
            type: 'OD',
            player1Name,
            player2Name,
        },
        'register OD team'
    );
}
